import { provideHttpClient, withInterceptorsFromDi, withXsrfConfiguration } from '@angular/common/http';
import { ApplicationConfig, importProvidersFrom, inject, LOCALE_ID, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { ApmModule } from '@elastic/apm-rum-angular';
import { AppBasicsModule, AppBasicsModuleConfigModel, AuthState, MdsModalModule, MfAuthGuard, MfCommonAuthModule } from '@meddev/fe-shared';
import { NgbDateAdapter, NgbDateNativeAdapter, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { withNgxsReduxDevtoolsPlugin } from '@ngxs/devtools-plugin';
import { withNgxsFormPlugin } from '@ngxs/form-plugin';
import { withNgxsLoggerPlugin } from '@ngxs/logger-plugin';
import { withNgxsRouterPlugin } from '@ngxs/router-plugin';
import { withNgxsStoragePlugin } from '@ngxs/storage-plugin';
import { provideStore } from '@ngxs/store';
import { SweetAlert2Module } from '@sweetalert2/ngx-sweetalert2';
import { NgxMaskDirective, provideEnvironmentNgxMask, provideNgxMask } from 'ngx-mask';
import { MatomoInitializationMode, MatomoModule } from 'ngx-matomo-client';
import { NgxPermissionsModule } from 'ngx-permissions';
import { ToastrModule } from 'ngx-toastr';
import { environment } from '../environments/environment';
import { ScriptLoaderService } from './_services/script-loader.service';
import { EnvironmentHelper } from './_shared/helper/environment-helper';
import { MedifitErrorDefinition } from './_shared/helper/medifit-error-definitions';
import { RulesService } from './_shared/services';
import { KeyboardShortcutsGlobalService } from './_shared/services/keyboard-shortcuts-global.service';
import { KeyboardShortcutsTinyMCEService } from './_shared/services/keyboard-shortcuts-tinymce.service';
import { MenuService } from './_shared/services/menu.service';
import { OneSignalService } from './_shared/services/onesignal.service';
import { BaseState } from './_shared/state/base/base.state';
import { CustomerState } from './_shared/state/customer/customer.state';
import { DeviceState } from './_shared/state/devices/device-state';
import { ExaminationGroupsState } from './_shared/state/examination-groups/examination-groups-state';
import { ExaminationState } from './_shared/state/examinations/examinations-state';
import { KanbanState } from './_shared/state/kanban/kanban.state';
import { LaboratoryState } from './_shared/state/laboratory/laboratory-state';
import { TermState } from './_shared/state/term/term.state';
import { TreatmentState } from './_shared/state/treatment/treatment.state';
import { WebsocketState } from './_shared/state/websocket/websocket.state';
import { routes } from './app.routes';
import { EambulantaUserInitService } from './auth/services/user-initialization.service';
import { PublicState } from './public/state/public.state';
import { LayoutModule } from './theme/layouts/layout.module';
import { TeethState } from './theme/pages/home/dentist/_state/teeth.state';
import { AvailabilityState } from './theme/pages/settings/availability-calendar/_state/availability.state';
import { TelemedicineModule } from './theme/pages/telemedicine/telemedicine.module';

EnvironmentHelper.initEnvironment();

export const appConfig: ApplicationConfig = {
    providers: [
        importProvidersFrom(
            ApmModule,
            NgbModule,
            LayoutModule,
            BrowserModule,
            ToastrModule.forRoot({
                positionClass: 'toast-top-center',
            }),
            SweetAlert2Module.forRoot(),
            TelemedicineModule,
            AppBasicsModule.forRoot({
                version: environment.VERSION,
                production: environment.production,
                releaseStage: environment.RELEASE_STAGE as AppBasicsModuleConfigModel['releaseStage'],
                matomo: {
                    siteId: '16',
                    trackerUrl: 'https://data.medifit.si/',
                },
                bugSnag: {
                    apiKey: '4c8b0c632c20c7d5d987858bb90936a3',
                },
                apmServiceName: 'eambulanta-app',
                errorDefinitions: {
                    errors: MedifitErrorDefinition.errors,
                } as AppBasicsModuleConfigModel['errorDefinitions'],
            }),
            MdsModalModule.forRoot({
                default: {
                    confirmButtonText: $localize`Potrdi`,
                    cancelButtonText: $localize`Zapri`,
                    denyButtonText: $localize`Prekliči`,
                },
                error: {
                    title: $localize`Napaka`,
                },
            }),
            MfCommonAuthModule.forRoot({
                authV1Url: environment.auth.v1.protocol + environment.auth.v1.rootUrl + environment.auth.v1.port + environment.auth.v1.url,
                authV2Url: environment.auth.v2.protocol + environment.auth.v2.rootUrl + environment.auth.v2.port + environment.auth.v2.url,
                authV2TokenPatterns: environment.mfCommonAuth.authV2TokenPatterns.map((p: string) => new RegExp(p)),
                loginRedirectUrl: environment.mfCommonAuth.loginRedirectUrl,
                realmName: environment.mfCommonAuth.realmName,
                clientId: environment.mfCommonAuth.clientId,
                impersonationTokenUrlParamName: environment.mfCommonAuth.impersonationTokenUrlParamName,
            }),
            MatomoModule.forRoot({
                mode: MatomoInitializationMode.AUTO_DEFERRED,
            }),
            NgxMaskDirective,
            NgxPermissionsModule.forRoot(),
        ),
        MfAuthGuard,
        provideRouter(routes),
        {
            provide: 'userInitializationService',
            useClass: EambulantaUserInitService,
        },
        ScriptLoaderService,
        RulesService,
        { provide: LOCALE_ID, useValue: 'sl' },
        { provide: NgbDateAdapter, useClass: NgbDateNativeAdapter },
        OneSignalService,
        NgxPermissionsModule,
        MenuService,
        provideEnvironmentNgxMask(),
        provideNgxMask(),
        provideHttpClient(withInterceptorsFromDi(), withXsrfConfiguration({})), // TODO do we need this, and setup withXsrfConfiguration?
        provideAnimations(),
        provideStore(
            [
                AuthState,
                BaseState,
                TreatmentState,
                WebsocketState,
                PublicState,
                TeethState,
                AvailabilityState,
                ExaminationState,
                LaboratoryState,
                DeviceState,
                KanbanState,
                ExaminationGroupsState,
                TermState,
                CustomerState,
            ],
            { developmentMode: !environment.production },
            withNgxsStoragePlugin({
                keys: '*',
            }),
            withNgxsRouterPlugin(),
            withNgxsFormPlugin(),
            withNgxsReduxDevtoolsPlugin({
                disabled: environment.production,
            }),
            withNgxsLoggerPlugin({
                disabled: environment.production,
            }),
        ),
        provideZoneChangeDetection({
            eventCoalescing: true,
            runCoalescing: true,
        }),
        provideAppInitializer(() => {
            inject(KeyboardShortcutsGlobalService)?.init();
            inject(KeyboardShortcutsTinyMCEService)?.init();
        }),
    ],
};
